import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { map, isString, pick, omit } from 'lodash'
import { Helmet } from 'react-helmet-async'
import { buildListUrl } from 'utils/url'
import styled from 'styled-components'
import Cookies from 'universal-cookie'
import { actionCreators as searchParamsActionCreators } from 'reduxBits/searchParams'

import { Breadcrumbs as BUIBreadcrumbs } from 'blocket-ui'

const Wrapper = styled.div`
  padding-top: 16px;
  padding-bottom: 8px;
`

export const buildBreadcrumbsProps = (
  breadcrumbs,
  query,
  searchParametersDefinition,
  geoPref,
  filterQuery,
) => {
  return breadcrumbs.map(({ searchParameters, label }) => {
    const apiQuery = queryString.parse(searchParameters)
    const isBilarCategory = apiQuery.cg === '1020'
    const listQuery = omit(
      {
        ...pick(geoPref, ['ag', 'r', 'm', 'sa']),
        ...apiQuery,
      },
      ['page'],
    )

    const breadcrumbQuery = isBilarCategory ? { ...listQuery, ...filterQuery } : listQuery

    const link = isString(searchParameters)
      ? buildListUrl(breadcrumbQuery, searchParametersDefinition)
      : ''

    return {
      link,
      label,
    }
  })
}

export const buildStructuredData = (breadcrumbs) => {
  if (typeof window === 'undefined') return null
  if (!breadcrumbs.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: map(breadcrumbs, (breadcrumb, i) => {
      let breadcrumbLink
      if (breadcrumb.link.length) {
        breadcrumbLink = `${window.location.origin}${breadcrumb.link}`
      } else {
        breadcrumbLink = window.location.href
      }
      return {
        '@type': 'ListItem',
        position: i + 1,
        name: breadcrumb.label,
        item: breadcrumbLink,
      }
    }),
  }
}

export const Breadcrumbs = ({
  breadcrumbs,
  searchParametersDefinition,
  geoPref,
  location: { search },
  filterQuery,
  dispatch,
  className,
}) => {
  const query = queryString.parse(search)
  useEffect(() => {
    if (query.cg && query.cg !== '1020') {
      const cookies = new Cookies()
      cookies.remove('filterQuery', { path: '/' })
      dispatch(searchParamsActionCreators.saveFilterParameters({}))
    }
  }, [dispatch, query.cg]) // Note: should be query.cg

  if (!breadcrumbs || breadcrumbs.length <= 0) {
    return null
  }

  const breadcrumbProps = buildBreadcrumbsProps(
    breadcrumbs,
    query,
    searchParametersDefinition,
    geoPref,
    filterQuery,
  )

  const structuredData = buildStructuredData(breadcrumbProps)

  return (
    <Wrapper className={className}>
      {structuredData && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>
      )}
      <BUIBreadcrumbs breadcrumbs={breadcrumbProps} />
    </Wrapper>
  )
}

Breadcrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
    }),
  ),
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  searchParametersDefinition: PropTypes.object.isRequired,
  geoPref: PropTypes.object,
  dispatch: PropTypes.func,
  filterQuery: PropTypes.object,
  searchParamsActions: PropTypes.shape({
    saveFilterParameters: PropTypes.func.isRequired,
  }),
  className: PropTypes.string,
}

export default withRouter(connect()(Breadcrumbs))
